/**
 * The Captains screen feature (M6 plan P32, spec §5, §8; the career in the Reach only, with the `fleet` service
 * from P21's fleet link and the strategic client). The Captains screen and the memorial are the dashboard's
 * **Fleet › Captains** section (dashboard spec §2.2, W3 card M2; `registerDashSection`). There is no flight key
 * (D-407): Tab, then Z/C or a click.
 *
 * `frame` allocates nothing except on the open and close events (D-014).
 */
import { FEATURE_ORDER, type FrameInfo, type GameContext, type GameFeature } from '../../app/feature'
import { createPadNav, stepPadNav, type PadNavState } from '../../hud/menu'
import { loadSettingsConfig } from '../../settings/settings'
import { openDashboard, registerDashSection } from '../../ui/dashboard/registry'
import { createCaptainsSection, type CaptainsSectionHost } from '../../ui/fleet/captainsHost'
import { fleetSection, FLEET_SECTION_IDS } from '../../ui/fleet/sectionHost'
import { loadCaptainsConfig, type CaptainNames } from '../../ui/fleet/models/captains'

/** After the Fleet screen (wire + 2), before the Charter HUD (530). */
const ORDER = FEATURE_ORDER.wire + 3

export interface CaptainsScreenFeature extends GameFeature {
  readonly screen: CaptainsSectionHost | null
  /** Opens the dashboard at Fleet › Captains (the debug API). */
  openCaptains(): void
  frame(ctx: GameContext, f: FrameInfo): void
  dispose(): void
}

/** Player station names (`pst-<seq>` → `PlayerStationView.name`, M6 P34), refreshed with every captain view. */
const playerStationNames = new Map<string, string>()

function reachNames(ctx: GameContext): CaptainNames {
  const systems = new Map((ctx.reach?.galaxy.systems ?? []).map((s) => [s.id, s.name]))
  return {
    system: (id) => systems.get(id) ?? id,
    station: (id) => playerStationNames.get(id) ?? id,
  }
}

export function createCaptainsScreenFeature(): CaptainsScreenFeature {
  let overlay: CaptainsSectionHost | null = null
  let nav: PadNavState | null = null
  const offs: (() => void)[] = []

  const feature: CaptainsScreenFeature = {
    name: 'captainsScreen',
    order: ORDER,
    get screen(): CaptainsSectionHost | null {
      return overlay
    },

    init(ctx: GameContext): void {
      const fleet = ctx.services.maybe('fleet')
      const client = ctx.services.maybe('strategic')
      if (fleet === undefined || client === undefined || ctx.reach === null) return
      const section = createCaptainsSection({
        service: {
          fleetView: () => fleet.fleetView(),
          run: (cmd) => fleet.run(cmd),
          captainView: async () => {
            const [view, own] = await Promise.all([client.captainView(null), client.playerStationViews().catch(() => [])])
            for (const st of own) playerStationNames.set(st.id, st.name)
            return view
          },
        },
        names: reachNames(ctx),
        config: loadCaptainsConfig(),
      })
      overlay = section
      nav = createPadNav(loadSettingsConfig().menuNav, ctx.data.bindings.gamepad.buttons.pause)
      offs.push(registerDashSection(fleetSection('captains', section.component)))
    },

    openCaptains(): void {
      openDashboard({ tab: 'fleet', section: FLEET_SECTION_IDS.captains })
    },

    frame(ctx: GameContext, f: FrameInfo): void {
      const o = overlay
      if (o === null || nav === null) return
      // Stepped every frame so the press that opened the tab is not seen again as a new one; B/Start are the shell's.
      const i = stepPadNav(nav, ctx.input.pad, f.dt)
      if (o.shown && i !== null && i !== 'back' && i !== 'start' && !f.paused) o.input(i)
    },

    dispose(): void {
      for (const off of offs.splice(0)) off()
      overlay = null
      nav = null
    },
  }
  return feature
}

const feature: CaptainsScreenFeature = createCaptainsScreenFeature()

export default feature
